import { useEffect } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { ChannelType, type Channel } from '@comms/shared';
import { applyDispatch, useStore } from '../store';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import { reloadNow, useUpdate } from '../lib/update';
import { ServerRail } from '../components/ServerRail';
import { GuildSidebar } from '../components/GuildSidebar';
import { DmSidebar } from '../components/DmSidebar';
import { UserPanel } from '../components/UserPanel';
import { VoiceChannelView, VoicePanel } from '../components/VoiceUi';
import { ChannelView } from './ChannelView';
import { ForumView } from './ForumView';
import { DmView } from './DmView';
import { FriendsView } from './FriendsView';
import { AdminPanel } from './AdminPanel';
import { Modals } from '../components/Modals';
import { IncomingCalls } from '../components/CallUi';
import { AppUpdateBar } from '../components/AppUpdateBar';
import { Spinner } from '../components/ui';
import { NavDrawer } from '../components/MobileNav';
import { useIsMobile, useSwipe } from '../hooks/mobile';
import { setNavOpen } from '../store/ui';

export function Layout({ admin }: { admin?: boolean }) {
  const { guildId = '@me', channelId } = useParams<{ guildId: string; channelId?: string }>();
  const connection = useStore((s) => s.connection);
  const update = useUpdate((s) => s.available);
  const ready = useStore((s) => s.me !== null);
  const guildExists = useStore((s) => guildId === '@me' || !!s.guilds[guildId]);
  const firstChannel = useStore((s) =>
    guildId === '@me'
      ? undefined
      : Object.values(s.channels)
          .filter((c) => c.guildId === guildId && c.type === ChannelType.GUILD_TEXT)
          .sort((a, b) => a.position - b.position)[0]?.id,
  );
  const e2eeError = useSession((s) => s.e2eeError);
  const channelType = useStore((s) => (channelId ? s.channels[channelId]?.type : undefined));
  const isVoice = channelType === ChannelType.GUILD_VOICE;
  const missing = useStore((s) => !!channelId && guildId !== '@me' && !s.channels[channelId]);
  const mobile = useIsMobile();
  // Phones: swipe right from the left edge to open servers & channels.
  const swipeRoot = useSwipe({ onRight: () => setNavOpen(true), rightFromEdgeOnly: true }, mobile);

  // Phones: the home screen (no conversation picked) starts on the navigation, like Discord.
  // Picking a channel or conversation closes it (see GuildSidebar / DmSidebar).
  useEffect(() => {
    if (mobile && guildId === '@me' && !channelId && !admin) setNavOpen(true);
  }, [mobile, guildId, channelId, admin]);

  // Archived threads aren't part of the guild snapshot: fetch them on demand.
  useEffect(() => {
    if (!missing || !channelId) return;
    api<Channel>('GET', `/channels/${channelId}`)
      .then((c) => applyDispatch('CHANNEL_UPDATE', c))
      .catch(() => {});
  }, [missing, channelId]);

  useEffect(() => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      const ask = () => void Notification.requestPermission();
      window.addEventListener('click', ask, { once: true });
      return () => window.removeEventListener('click', ask);
    }
  }, []);

  if (!ready) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 bg-bg-0 text-text-2">
        <Spinner className="h-8 w-8" />
        <span>Connecting…</span>
      </div>
    );
  }
  if (!guildExists) return <Navigate to="/channels/@me" replace />;
  if (guildId !== '@me' && !channelId && firstChannel)
    return <Navigate to={`/channels/${guildId}/${firstChannel}`} replace />;

  const nav = (
    <>
      <ServerRail activeGuildId={admin ? null : guildId} />
      <div className="flex min-w-0 flex-1 flex-col bg-bg-1 md:w-60 md:flex-none md:shrink-0">
        {guildId === '@me' || admin ? (
          <DmSidebar activeChannelId={channelId} />
        ) : (
          <GuildSidebar guildId={guildId} activeChannelId={channelId} />
        )}
        <VoicePanel />
        <UserPanel />
      </div>
    </>
  );

  return (
    <div ref={swipeRoot} className="flex h-full overflow-hidden">
      {mobile ? <NavDrawer>{nav}</NavDrawer> : nav}
      <main className="safe-y flex min-w-0 flex-1 flex-col bg-bg-2 md:pb-0 md:pt-0">
        {connection === 'reconnecting' && (
          <div className="bg-warning px-4 py-1 text-center text-sm font-medium text-black">
            Reconnecting…
          </div>
        )}
        {update ? (
          <div
            role="status"
            className="flex items-center justify-center gap-3 bg-accent px-4 py-1 text-sm text-white"
          >
            A new version of the app is available.
            <button type="button" onClick={reloadNow} className="font-semibold underline">
              Reload
            </button>
          </div>
        ) : (
          <AppUpdateBar />
        )}
        {e2eeError && (
          <div className="bg-danger px-4 py-1 text-center text-sm text-white">
            Encryption unavailable on this device: {e2eeError}
          </div>
        )}
        {admin ? (
          <AdminPanel />
        ) : guildId === '@me' ? (
          channelId ? (
            <DmView key={channelId} channelId={channelId} />
          ) : (
            <FriendsView />
          )
        ) : channelId && isVoice ? (
          <VoiceChannelView key={channelId} guildId={guildId} channelId={channelId} />
        ) : channelId && channelType === ChannelType.GUILD_FORUM ? (
          <ForumView key={channelId} guildId={guildId} channelId={channelId} />
        ) : channelId ? (
          <ChannelView key={channelId} guildId={guildId} channelId={channelId} />
        ) : (
          <div className="flex flex-1 items-center justify-center text-text-3">
            No text channels yet.
          </div>
        )}
      </main>
      <Modals />
      <IncomingCalls />
    </div>
  );
}
